import {
  Component,
  OnInit,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  BuyerSurveyResponse,
  SurveyRequest,
} from '../../shared/interfaces/survey.interface';
import {
  CountryInterface,
  MultiCountryDraftData,
} from '../../shared/interfaces/create-surveys.interface';
import { Subscription } from 'rxjs';
import { CreateSurveyService } from '../create-survey.service';
import { ActivatedRoute } from '@angular/router';
import { SURVEY_STATUS } from '../../shared/constants/survey-status';
import { SurveyStatus } from '../../shared/utils/survey-status';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { QuotaPurePrice } from '../pure-price-window/pure-price.interface';
import { MetaDataToPurePrice } from '../../shared/interfaces/black-box.interface';
import { AuthService } from '../../shared/services/auth/auth.service';

@Component({
  selector: 'ps-ui-right-slider-window',
  templateUrl: './right-slider-window.component.html',
  styleUrls: ['./right-slider-window.component.css'],
})
export class RightSliderWindowComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() surveyDetails?: BuyerSurveyResponse;
  @Input() public purePriceData!: MetaDataToPurePrice;
  @Input() hasQuotasForPurePrice: boolean = false;
  @Input() isTCSPAEditMode: boolean = false;
  @Input() countries: CountryInterface[] = [];
  @Input() mcSurveyPayload = <SurveyRequest>{};
  @Input() mcSurveyAddType: string = '';
  @Input() isMultiCountryTemplate: boolean = false;
  @Output() purePriceApplied = new EventEmitter<QuotaPurePrice>();
  @Output() purePriceV2Applied = new EventEmitter<number>();
  @Output() mcSurveyAdded = new EventEmitter();
  @Input() showFlyoutWindow: boolean = false;
  public purePriceWindow: boolean = false;
  public appliedQuotaName?: string;
  private _subscriptions: Subscription = new Subscription();
  multiCountryDraftData: MultiCountryDraftData = {
    countryData: [],
  };
  multiCountryDraft: boolean = false;
  surveyId!: number;
  surveyStatus = SURVEY_STATUS;
  showFlyingWindow: boolean = false;
  constructor(
    private _createSurveyService: CreateSurveyService,
    private _route: ActivatedRoute,
    private _buyerApiService: BuyerApiService,
    private _authService: AuthService
  ) {}

  ngOnInit(): void {
    this._subscriptions.add(
      this._route.params?.subscribe((params) => {
        if (params['id']) {
          this.surveyId = Number(params['id']);
        } else {
          this._createSurveyService.setMultiCountrySurveys([]);
        }
      })
    );

    this._subscriptions.add(
      this._createSurveyService.getMultiCountryData().subscribe((data) => {
        this.multiCountryDraft = data.isDraft;
        this.showFlyingWindow = data.isDraft;
      })
    );
    this._subscriptions.add(
      this._createSurveyService
        .getMultiCountrySurveys()
        .subscribe((countryData) => {
          this.multiCountryDraftData = {
            countryData,
          };
          this.multiCountryDraft = this._checkIfFlyoutShouldBeOpenForMc();
        })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['surveyDetails']) {
      this.appliedQuotaName = this.surveyDetails?.qbp?.category;
    }
    this.purePriceWindow = this._checkIfFlyoutShouldBeOpenForPurePrice();
    if (
      changes.surveyDetails?.currentValue &&
      this.surveyDetails?.ps_survey_status === this.surveyStatus.DRAFT
    ) {
      if (!this.surveyDetails?.mc_unique_id) {
        this._createSurveyService.setMultiCountrySurveys([]);
      }
      this._manageMultiCountrySurvey();
    }
  }

  private _checkIfFlyoutShouldBeOpen() {
    const isFlyoutOpenForMc = this._checkIfFlyoutShouldBeOpenForMc();
    if (isFlyoutOpenForMc) {
      return true;
    }
    const isFlyoutOpenForPurePrice =
      this._checkIfFlyoutShouldBeOpenForPurePrice();
    return isFlyoutOpenForPurePrice;
  }

  private _checkIfFlyoutShouldBeOpenForMc() {
    return !!(
      this.multiCountryDraftData &&
      this.multiCountryDraftData.countryData &&
      this.multiCountryDraftData.countryData.length
    );
  }

  private _checkIfFlyoutShouldBeOpenForPurePrice() {
    return (
      !!(
        this.surveyDetails?.ps_survey_status === this.surveyStatus.DRAFT &&
        !this.isTCSPAEditMode &&
        (this.purePriceData?.survey?.is_pure_price_rate_card_enable ||
          (this._authService.buyerConfig?.enablePriceRecomendation &&
            this.hasQuotasForPurePrice &&
            this.purePriceData?.quotas?.length))
      ) && !this.showFlyoutWindow
    );
  }

  applyPurePrice(quotaPurePriceApplied: QuotaPurePrice) {
    this.purePriceApplied.emit(quotaPurePriceApplied);
  }
  public applyPurePriceV2(fieldTime: number) {
    this.purePriceV2Applied.emit(fieldTime);
  }

  private _manageMultiCountrySurvey() {
    if (this.surveyDetails?.mc_unique_id) {
      const mcSurveys = this.multiCountryDraftData.countryData;
      const isSurveyExists = mcSurveys.find(
        (mcSurvey) =>
          mcSurvey.surveyId === Number(this.surveyDetails?.ps_survey_id)
      ); // check if survey already exists in multi country flyout
      if (!isSurveyExists) {
        // if survey does not exist then populate all surveys with same mc_unique_id
        let surveyStatus = Object.values(SurveyStatus).filter(
          (status) => typeof status === 'number'
        );
        this._buyerApiService
          .getSurveys(
            surveyStatus.join(','),
            1,
            '',
            this.surveyDetails?.mc_unique_id,
            1
          )
          .subscribe((surveys) => {
            const mappedSurveys =
              this._createSurveyService.mapMultiCouuntrySurveyData(surveys);
            this._createSurveyService.setMultiCountrySurveys(mappedSurveys);
            this._createSurveyService.setIsDraftSavedValue(true);
          });
      }
    }
  }

  openFlyingOutWindow(isManualClose = false) {
    if (this.multiCountryDraftData.countryData.length) {
      this.multiCountryDraft = true;
    }
    this.showFlyingWindow = !isManualClose;
  }

  informOnMcSurveyAdded() {
    this.mcSurveyAdded.emit();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
